import BorderAllIcon from '@mui/icons-material/BorderAll'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import DescriptionIcon from '@mui/icons-material/Description'
import DrawIcon from '@mui/icons-material/Draw'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import FolderZipIcon from '@mui/icons-material/FolderZip'
import ImageIcon from '@mui/icons-material/Image'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import MailOutlineIcon from '@mui/icons-material/MailOutlined'
import NotesIcon from '@mui/icons-material/Notes'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import Avatar from '@mui/material/Avatar'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import LinearProgress from '@mui/material/LinearProgress'
import TextField from '@mui/material/TextField'

import { extractFileExtension } from '@util/string-util'

import { SwappedTypography } from '../../../../theme/components/SwappedTypography'
import type { UploadFile } from '../../types/UploadFile'

export { UploadFileCard }

type Props = {
  uploadFile: UploadFile
  state?: 'loading' | 'done' | 'error'
  onOmschrijvingChange: (fileName: string, description: string) => void
}

const generateFileDetails = (selectedFile: any) => {
  const extension = extractFileExtension(selectedFile.name)

  let Icon = InsertDriveFileIcon
  let backgroundColor = '#aaaaaa'
  let sublabel = 'bestand'

  if (!extension) {
    return {
      Icon,
      backgroundColor,
      sublabel,
    }
  }

  if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
    Icon = ImageIcon
    backgroundColor = '#2196f3'
    sublabel = 'afbeelding'
  }

  if (['pdf'].includes(extension)) {
    Icon = PictureAsPdfIcon
    backgroundColor = '#f44336'
    sublabel = 'PDF'
  }

  if (['doc', 'docx'].includes(extension)) {
    Icon = DescriptionIcon
    backgroundColor = '#009688'
    sublabel = 'document'
  }

  if (['zip'].includes(extension)) {
    Icon = FolderZipIcon
    backgroundColor = '#03a9f4'
    sublabel = 'zip'
  }

  if (['xls', 'xlsx'].includes(extension)) {
    Icon = BorderAllIcon
    backgroundColor = '#4caf50'
    sublabel = 'spreadsheet'
  }

  if (['msg'].includes(extension)) {
    Icon = MailOutlineIcon
    backgroundColor = '#4caf50'
    sublabel = 'bericht'
  }

  if (['dwg'].includes(extension)) {
    Icon = DrawIcon
    backgroundColor = '#9c27b0'
    sublabel = 'tekening'
  }

  if (['txt', 'md'].includes(extension)) {
    Icon = NotesIcon
    backgroundColor = '#ffeb3b'
    sublabel = 'tekstbestand'
  }

  return {
    Icon,
    backgroundColor,
    sublabel,
  }
}

const UploadFileCard = ({ uploadFile, state, onOmschrijvingChange }: Props) => {
  const { Icon, backgroundColor, sublabel } = generateFileDetails(uploadFile)
  const { name, originalName, omschrijving } = uploadFile
  const nameChanged = name !== originalName

  return (
    <Card
      key={name}
      variant="outlined"
      elevation={0}
      sx={{ background: 'rgba(0,0,0,.01)' }}
    >
      <CardHeader
        avatar={
          <Avatar sx={{ backgroundColor }}>
            <Icon />
          </Avatar>
        }
        title={
          nameChanged ? (
            <SwappedTypography
              oldText={originalName}
              newText={name}
              reason="Bestandsnaam automatisch gewijzigd vanwege verboden karakters"
            />
          ) : (
            name
          )
        }
        subheader={sublabel}
        action={
          state === 'done' ? (
            <CheckIcon color="success" />
          ) : state === 'error' ? (
            <ClearIcon color="error" />
          ) : (
            <FiberManualRecordIcon sx={{ opacity: '0.1' }} />
          )
        }
      />

      <CardContent sx={{ paddingTop: 0 }}>
        <TextField
          fullWidth
          multiline
          variant="standard"
          label={`Omschrijving (${omschrijving.length}/255)`}
          onChange={(e) => onOmschrijvingChange(name, e.target.value)}
          slotProps={{ htmlInput: { maxLength: 255 } }}
        />
      </CardContent>

      {state === 'loading' && <LinearProgress />}
    </Card>
  )
}
